export enum PayoutPeriodChoice {
  PostFulfillment = 'POST_FULFILLMENT',
  PostOrder = 'POST_ORDER',
  PostReturnPeriod = 'POST_RETURN_PERIOD'
}

export enum ProductReviewAction {
  ContentReview = 'CONTENT_REVIEW',
  FullReview = 'FULL_REVIEW',
  None = 'NONE'
}

export enum ShippingCountryOfOriginOption {
  ShopCountryOnly = 'SHOP_COUNTRY_ONLY',
  SellerCountryOnly = 'SELLER_COUNTRY_ONLY',
  AllCountries = 'ALL_COUNTRIES',
  CustomList = 'CUSTOM_LIST'
}

export enum SettingScope {
  Product = 'PRODUCT',
  Seller = 'SELLER',
  Shop = 'SHOP'
}

export enum ProductTypeOptions {
  Physical = 'PHYSICAL',
  Digital = 'DIGITAL',
  Both = 'BOTH'
}
export enum InlineShippingOptions {
  DomesticOnly = 'DOMESTIC_ONLY',
  InternationalMandatory = 'INTERNATIONAL_MANDATORY',
  InternationalOptional = 'INTERNATIONAL_OPTIONAL'
}

export enum SellerLocationSettingOption {
  ShopCountryOnly = 'SHOP_COUNTRY_ONLY',
  AllCountries = 'ALL_COUNTRIES',
  CustomList = 'CUSTOM_LIST'
}

export enum DeductionType {
  Percentage = 'PERCENTAGE',
  Flat = 'FLAT'
}

export enum CommissionBasis {
  NetSales = 'NET_SALES',
  Profit = 'PROFIT'
}


export type StorefrontSettingOptions = {
  allowNotification: boolean;
  allowBanner: boolean;
  allowStory: boolean;
  allowOwnerBio: boolean;
  allowReviews: boolean;
  allowMessaging: boolean;
}

export type StorefrontSetting = {
  enabled: boolean;
  storefrontOptions: StorefrontSettingOptions;
}

export type SellerOrderAcceptSetting = {
  autoAccept: boolean;
  orderAcceptTimeout: number;
}

export type SellerOrderFeeSetting = {
  orderFee: number;
}

export type SellerOrderFulfilledSetting = {
  autoFulfilled: boolean;
  orderFulfilledTimeout: number;
}

export type PayoutPeriodSetting = {
  payoutPeriod: number;
  payoutPeriodChoice: PayoutPeriodChoice;
}

export type ProductFeeSetting = {
  productFee: number;
}

export type ProductPercentageSetting = {
  productPercentage: number;
}

export type ReturnsSetting = {
  noReturns: boolean;
  returnsTimeout: number;
}

export type ShippingPercentageSetting = {
  shippingPercentage: number;
}

/**
 * A named charge subtracted from a seller's payout, on top of the commission
 * fees. PERCENTAGE amounts apply to the seller's order subtotal; FLAT amounts
 * are taken as-is.
 *
 * Scoping is REPLACE-ONLY: a seller-scoped list wholly replaces the shop-scoped
 * list, because the settings cascade resolves a whole setting object
 * (`effective: current || fallback`) and never merges.
 */
export type Deduction = {
  name: string;
  type: DeductionType;
  amount: number;
}

export type DeductionsSetting = {
  deductions: Deduction[];
}

/**
 * What the product commission percentage is charged on.
 *
 * NET_SALES (default) charges on the sale price. PROFIT charges on
 * (price − cost of goods), where the cost is mirrored from Shopify's merchant-owned
 * "Cost per item" — sellers cannot set it, so the basis can't be gamed by the party
 * paying the commission. A product with no cost falls back to NET_SALES.
 *
 * `showSellerCost` decides whether sellers see the underlying cost and profit
 * breakdown; enforced server-side in the partner mapper, not just in the UI.
 */
export type CommissionBasisSetting = {
  basis: CommissionBasis;
  showSellerCost: boolean;
}

export type ProductReviewSetting = {
  reviewAction: ProductReviewAction;
} 

export type ProductTypeSetting = {
  productType: ProductTypeOptions;
}

export type ProductShippingSetting = {
  allowProfiles: boolean;
  inlineShippingOptions: InlineShippingOptions;
  shippingCountryOfOriginOption: ShippingCountryOfOriginOption;
  customShippingCountryOfOriginList: string[];
  hideSelectCountryIfSingleCountry: boolean;
  allowAllInternationalShippingCountries: boolean;
  allowedInternationalShippingCountries: string[];
}

export type ProductVariantsSetting = {
  allowVariants: boolean;
}


export type SellerLocationSetting = {
  allowedCountries: string[];
  hideSelectCountryIfSingleCountry: boolean;
  sellerLocationOption: SellerLocationSettingOption;
}

export type ProductPublishSetting = {
  activateOnAccept: boolean;
  publishOnlineOnAccept: boolean;
}

